type Pages = { [key: string]: {} };
export async function isPathMatch(path: string, pages: Pages, baseDevice: string = ''): Promise<boolean> {
  // Remove leading and trailing slashes and split into segments
  const normalizedPath = path.replace(/^\/|\/$/g, '');
  const pathSegments = normalizedPath.split('/');

  // Convert pages object keys into a more manageable format
  const pagePatterns = Object.keys(pages).map(page => {
    // Remove the device prefix (web/ or mobile/) if it exists
    const withoutDevice = page.replace(/^\/?(web|mobile)\//, '');
   
    
    return withoutDevice.replace(/^\/|\/$/g, '').split('/');
  });
  
  
  // Function to check if a segment matches a pattern
  const isSegmentMatch = (segment: string, pattern: string): boolean => {
    // If pattern is wrapped in [], it's dynamic
    if (pattern.startsWith('[') && pattern.endsWith(']')) {
      // Handle catch-all routes [...slug]
      if (pattern.startsWith('[...')) {
        return true;
      }
      // Handle optional catch-all routes [[...slug]]
      if (pattern.startsWith('[[...')) {
        return true;
      }
      // Handle regular dynamic routes [id]
      return true;
    }
    // For static segments, do exact match
    return segment === pattern;
  };

  // Check path against each page pattern
  return pagePatterns.some(pageSegments => {
    
    // If segments length doesn't match (except for catch-all routes), it's not a match
    if (pageSegments.length !== pathSegments.length) {
      // Check if the page has a catch-all route
      const hasCatchAll = pageSegments.some(segment =>
        segment.startsWith('[...') || segment.startsWith('[[...')
      );
      if (!hasCatchAll) return false;
    }
    
    // Check each segment
    return pageSegments.every((pattern, index) => {
      const segment = pathSegments[index];
      if (!segment && pattern.startsWith('[[')) {
        // Optional catch-all route with no value is valid
        return true;
      }
      if (!segment) return false;
      const isMatched = isSegmentMatch(segment, pattern);
  
      return isMatched;
    });
  });
}

export async function findMatchingPath(path: string, pages: any[], baseDevice: string = ''): Promise<object | null> {
  // Remove leading and trailing slashes and split into segments
  const normalizedPath = path.replace(/^\/|\/$/g, '');
  const pathSegments = normalizedPath.split('/');

  // Convert pages object keys into a more manageable format
  const pagePatterns = pages.map(page => {
    // Remove the device prefix (web/ or mobile/) if it exists
    //   const withoutDevice = page.replace(/^\/?(web|mobile)\//, '');

    const withoutDevice = page.path;
    return {
      originalPath: page.path,
      segments: withoutDevice.replace(/^\/|\/$/g, '').split('/'),
      pageObj:page
    };
  });

  // Function to check if a segment matches a pattern
  const isSegmentMatch = (segment: string, pattern: string): boolean => {
    // If pattern is wrapped in [], it's dynamic
    if (pattern.startsWith('[') && pattern.endsWith(']')) {
      // Handle catch-all routes [...slug]
      if (pattern.startsWith('[...')) {
        return true;
      }
      // Handle optional catch-all routes [[...slug]]
      if (pattern.startsWith('[[...')) {
        return true;
      }
      // Handle regular dynamic routes [id]
      return true;
    }
    // For static segments, do exact match
    return segment === pattern;
  };

  // Check path against each page pattern
  for (const { originalPath, segments: pageSegments,pageObj } of pagePatterns) {
    
    // If segments length doesn't match (except for catch-all routes), it's not a match
    if (pageSegments.length !== pathSegments.length) {
      // Check if the page has a catch-all route
      const hasCatchAll = pageSegments.some((segment:any) =>
        segment.startsWith('[...') || segment.startsWith('[[...')
      );
      if (!hasCatchAll) continue;
    }

    // Check each segment
    const isMatch = pageSegments.every((pattern:any, index:number) => {
      const segment = pathSegments[index];
      if (!segment && pattern.startsWith('[[')) {
        // Optional catch-all route with no value is valid
        return true;
      }
      if (!segment) return false;
      return isSegmentMatch(segment, pattern);
    });

    if (isMatch) {
      return pageObj // Return the matching path
    }
  }

  return null; // No match found
}

export async function extractDynamicValues(dynamicPath: string, resolvedUrl: string): Promise<Record<string, string>> {
  const dynamicSegments = dynamicPath.split("/").filter(Boolean); // Remove empty strings
  const resolvedSegments = resolvedUrl.split("/").filter(Boolean);

  const dynamicValues: Record<string, string> = {};
  dynamicSegments.forEach((segment, index) => {
    if (segment.startsWith("[") && segment.endsWith("]")) {
      const key = segment.slice(1, -1); // Extract key name (e.g., "id" from "[id]")
      dynamicValues[key] = resolvedSegments[index];
    }
  });

  return dynamicValues;
}

type ValueType = "GLOBAL" | "REDUX" | "LAYOUT";

interface ParsedResult {
  [key: string]: ValueType;
}

export async function parseUrlPattern(url: string): Promise<ParsedResult> {
  const result: ParsedResult = {};
  
  // Regular expression to match patterns like [key:value]
  const pattern = /\[([^:\]]+):([^\]]+)\]/g;
  
  // Find all matches in the URL
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(url)) !== null) {
    const [, key, value] = match;
    
    // Type guard to ensure value is of type ValueType
    if (isValidValue(value)) {
      result[key] = value;
    } else {
      throw new Error(`Invalid value type: ${value}. Expected GLOBAL, REDUX, or LAYOUT`);
    }
  }
  
  return result;
}

// Type guard function to check if a value is of type ValueType
function isValidValue(value: string): value is ValueType {
  return ["GLOBAL", "REDUX", "LAYOUT"].includes(value);
}

export const replacePlaceholders = async(template: string = "", values: Record<string, string>): Promise<string> => {
  return template.replace(/\[(\w+)\]/g, (_, key) => values[key] ?? "");
}